import React, { useState } from "react";
import { useLaneCopy } from "../../../utils/laneCopy";
import { useAppLanguage } from "../../../utils/appLanguage";
import i18n from "../../../i18n";
import { api } from "../../../services/api";
import { useIndonesiaV2Language } from "./indonesiaV2Language";

// ============ TYPES ============
type Step = "home" | "questions" | "input" | "result";
type Language = "en" | "id";

interface QuestionAnswers {
  [key: number]: string;
}

interface FormData {
  [key: string]: string | string[] | Date | TeamMember[];
}

interface TeamMember {
  name: string;
  dob: string;
  role: string;
}

const emptyTeamMember = (): TeamMember => ({ name: "", dob: "", role: "" });
const MAX_TEAM_MEMBERS = 20;

interface ResultData {
  [key: string]: string;
}

interface VisualCompatibilityItem {
  id: string;
  label: string;
  value_type: "percentage" | "status" | "short_label";
  icon: string;
  options?: string[];
}

interface MiniCard {
  id: string;
  title: string;
  icon: string;
  bind_to: string;
}

interface ModuleSections {
  [key: string]: string;
}

interface ModuleInputs {
  [key: string]: "date" | "text" | "list" | "select" | "member_list";
}

interface Module {
  id: string;
  title: string;
  subtitle: string;
  color: string;
  icon: string;
  guided_questions: string[];
  inputs: ModuleInputs;
  sections: ModuleSections;
  visual_compatibility?: {
    items: VisualCompatibilityItem[];
  };
  mini_cards: MiniCard[];
  ctas: string[];
}

// ============ TRANSLATIONS ============
const TRANSLATIONS = {
  en: {
    appName: "Astria",
    tagline:
      "Discover insights about your relationship with a modern, meaningful touch",
    selectModule: "Select Module",
    chooseTopic: "Choose the topic that suits your needs",
    clickToStart: "Click to start",
    back: "Back",
    backToQuestions: "Back to Questions",
    backToForm: "Back to Form",
    backToMenu: "Back to Menu",
    continue: "Continue",
    continueToData: "Continue to Personal Data",
    answerAll: "Answer all questions first ✨",
    fillData: "Fill in the data for more personal results ✨",
    seeResults: "See Results",
    processing: "Processing...",
    resultsForYou: "✨ Results for you",
    question: "Question",
    questions: "Questions",
    select: "Select...",
    tellHonestly: "Tell honestly...",
    enterName: "Enter names (separate with commas)",
    enter: "Enter",
    indonesia: "Indonesia",
    year: "2026",
    phase: "What phase are you in right now?",
    confusion: "What confuses you most about this relationship?",
    type: "Do you want to see TF, Soulmate, or Karmic?",
    behavior: "What behavior has been draining you lately?",
    feeling: "Does this relationship make you feel calmer or more anxious?",
    pattern: "Do you want to see NPD patterns, trauma bonding, or red flags?",
    improvement: "What do you want to improve in your relationship?",
    mood: "Have you been more calm or tense lately?",
    focus: "Do you want to see growth zone or emotional rhythm?",
    teamPhase: "What phase is your team in?",
    leader: "Who is the leader?",
    challenge: "What is the team's biggest challenge right now?",
    dobUser: "Your Date of Birth",
    dobPartner: "Partner's Date of Birth",
    relationshipSituation: "Relationship Situation",
    partnerBehavior: "Partner's Behavior",
    userFeelings: "Your Feelings",
    improvementGoal: "Improvement Goal",
    teamMembers: "Team Members",
    teamSituation: "Team Situation",
    memberLabel: "Member",
    memberName: "Name",
    memberNamePlaceholder: "Enter name",
    memberDob: "Date of Birth",
    memberRole: "Role",
    memberRolePlaceholder: "e.g. Designer, Manager",
    addMemberBtn: "+ Add Another Member",
    removeMember: "Remove",
    // Module Titles
    tfTitle: "Twin Flames & Soulmate",
    tfSubtitle: "See your inner connection and relationship rhythm.",
    npdTitle: "NPD & Toxic Dynamics",
    npdSubtitle:
      "Recognize relationship patterns that drain you and how to break free.",
    growthTitle: "Relationship Growth",
    growthSubtitle:
      "Build your relationship through energy, birth dates, and your story.",
    teamTitle: "Energy Match (Team)",
    teamSubtitle:
      "See team dynamics and how to work together more harmoniously.",
    // Module Sections
    softSummary: "Soft Summary",
    emotionalPulse: "Emotional Pulse",
    connectionType: "Connection Type",
    growthZone: "Growth Zone",
    gentleSuggestion: "Gentle Suggestion",
    emotionalRhythm: "Emotional Rhythm",
    energyDynamics: "Energy Dynamics",
    toxicPattern: "Toxic Pattern",
    clarityZone: "Clarity Zone",
    gentleDirection: "Gentle Direction",
    teamPulse: "Team Pulse",
    leaderDynamics: "Leader Dynamics",
    harmonyZone: "Harmony Zone",
    // CTAs
    weeklyRhythm: "Want to see this week's rhythm?",
    checkCommunication: "Want to check your communication patterns?",
    checkTraumaBonding: "Want to check if this is trauma bonding?",
    monthlyRhythm: "Want to see this month's rhythm?",
    addTeamMember: "Add team member?",
    weeklyDynamics: "Want to see this week's dynamics?",
    // Placeholders
    relationshipPlaceholder: "Describe your relationship situation...",
    behaviorPlaceholder: "Describe your partner's behavior...",
    feelingsPlaceholder: "Describe how you feel...",
    goalPlaceholder: "What do you want to improve?",
    teamSituationPlaceholder: "Describe the team situation...",
    rolePlaceholder: "Enter role (e.g., Project Manager)",
    // Options
    phaseOptions: ["Just met", "Close", "Distant", "Broken up"],
    moodOptions: ["Calm", "Tense"],
    focusOptions: ["Growth Zone", "Emotional Rhythm"],
    teamPhaseOptions: ["New", "Stable", "Conflict", "Growth"],
    connectionOptions: ["TF", "Soulmate", "Karmic"],
    patternOptions: ["NPD", "Trauma Bonding", "Red Flags"],
    statusOptions: ["Stable", "Rising", "Falling"],
    vibeOptions: ["Deep", "Intense", "Soft"],
  },
  id: {
    appName: "Astria",
    tagline:
      "Temukan wawasan tentang hubunganmu dengan sentuhan modern dan penuh makna",
    selectModule: "Pilih Modul",
    chooseTopic: "Pilih topik yang sesuai dengan kebutuhan kamu",
    clickToStart: "Klik untuk mulai",
    back: "Kembali",
    backToQuestions: "Kembali ke Pertanyaan",
    backToForm: "Kembali ke Form",
    backToMenu: "Kembali ke Menu",
    continue: "Lanjut",
    continueToData: "Lanjut ke Data Diri",
    answerAll: "Jawab semua pertanyaan dulu ya ✨",
    fillData: "Lengkapi data untuk hasil yang lebih personal ✨",
    seeResults: "Lihat Hasil",
    processing: "Memproses...",
    resultsForYou: "✨ Hasil bacaan untukmu",
    question: "Pertanyaan",
    questions: "Pertanyaan",
    select: "Pilih...",
    tellHonestly: "Ceritakan dengan jujur ya...",
    enterName: "Masukkan nama (pisahkan dengan koma)",
    enter: "Masukkan",
    indonesia: "Indonesia",
    year: "2026",
    phase: "Kalian lagi di fase apa sekarang?",
    confusion: "Apa yang paling bikin kamu bingung tentang hubungan ini?",
    type: "Kamu pengen lihat TF, Soulmate, atau Karmic?",
    behavior: "Apa perilaku yang paling bikin kamu capek belakangan ini?",
    feeling:
      "Kamu merasa hubungan ini bikin kamu makin tenang atau makin gelisah?",
    pattern: "Kamu pengen lihat pola NPD, trauma bonding, atau red flags?",
    improvement: "Hal apa yang ingin kamu perbaiki dalam hubungan kalian?",
    mood: "Kalian lebih sering tenang atau tegang akhir-akhir ini?",
    focus: "Kamu pengen lihat growth zone atau emotional rhythm?",
    teamPhase: "Tim kalian lagi di fase apa?",
    leader: "Siapa leadernya?",
    challenge: "Apa tantangan terbesar tim sekarang?",
    dobUser: "Tanggal Lahir Kamu",
    dobPartner: "Tanggal Lahir Pasangan",
    relationshipSituation: "Situasi Hubungan",
    partnerBehavior: "Perilaku Pasangan",
    userFeelings: "Perasaan Kamu",
    improvementGoal: "Tujuan Perbaikan",
    teamMembers: "Anggota Tim",
    teamSituation: "Situasi Tim",
    memberLabel: "Anggota",
    memberName: "Nama",
    memberNamePlaceholder: "Masukkan nama",
    memberDob: "Tanggal Lahir",
    memberRole: "Peran",
    memberRolePlaceholder: "cth. Desainer, Manajer",
    addMemberBtn: "+ Tambah Anggota Lain",
    removeMember: "Hapus",
    tfTitle: "Twin Flames & Soulmate",
    tfSubtitle: "Lihat koneksi batin kalian dan ritme hubungan kalian.",
    npdTitle: "NPD & Toxic Dynamics",
    npdSubtitle:
      "Kenali pola hubungan yang bikin kamu lelah dan cara keluar dari siklusnya.",
    growthTitle: "Relationship Growth",
    growthSubtitle:
      "Bangun hubungan lewat energi, tanggal lahir, dan cerita kalian.",
    teamTitle: "Energy Match (Team)",
    teamSubtitle:
      "Lihat dinamika tim dan cara bikin kerja bareng lebih harmonis.",
    softSummary: "Ringkasan Lembut",
    emotionalPulse: "Detak Emosional",
    connectionType: "Jenis Koneksi",
    growthZone: "Zona Tumbuh",
    gentleSuggestion: "Saran Lembut",
    emotionalRhythm: "Ritme Emosional",
    energyDynamics: "Dinamika Energi",
    toxicPattern: "Pola Toksik",
    clarityZone: "Zona Kejelasan",
    gentleDirection: "Arah Lembut",
    teamPulse: "Detak Tim",
    leaderDynamics: "Dinamika Pemimpin",
    harmonyZone: "Zona Harmoni",
    weeklyRhythm: "Mau lihat ritme minggu ini?",
    checkCommunication: "Mau cek pola komunikasi kalian?",
    checkTraumaBonding: "Mau cek apakah ini trauma bonding?",
    monthlyRhythm: "Mau lihat ritme bulan ini?",
    addTeamMember: "Tambah anggota tim?",
    weeklyDynamics: "Mau lihat dinamika minggu ini?",
    relationshipPlaceholder: "Ceritakan situasi hubunganmu...",
    behaviorPlaceholder: "Ceritakan perilaku pasanganmu...",
    feelingsPlaceholder: "Ceritakan perasaanmu...",
    goalPlaceholder: "Apa yang ingin kamu perbaiki?",
    teamSituationPlaceholder: "Ceritakan situasi tim...",
    rolePlaceholder: "Masukkan peran (contoh: Project Manager)",
    phaseOptions: ["Baru kenal", "Dekat", "Jauh", "Putus"],
    moodOptions: ["Tenang", "Tegang"],
    focusOptions: ["Growth Zone", "Emotional Rhythm"],
    teamPhaseOptions: ["Baru", "Stabil", "Konflik", "Growth"],
    connectionOptions: ["TF", "Soulmate", "Karmic"],
    patternOptions: ["NPD", "Trauma Bonding", "Red Flags"],
    statusOptions: ["Stabil", "Naik", "Turun"],
    vibeOptions: ["Deep", "Intense", "Soft"],
  },
};

// ============ CONFIG DATA ============
const MODULE_CONFIG: { modules: { [key: string]: Module } } = {
  modules: {
    twin_flames_soulmate_karmic: {
      id: "tf_sm_karmic_v1",
      title: "Twin Flames & Soulmate",
      subtitle: "Lihat koneksi batin kalian dan ritme hubungan kalian.",
      color: "lavender",
      icon: "💞",
      guided_questions: [
        "Kalian lagi di fase apa sekarang?",
        "Apa yang paling bikin kamu bingung tentang hubungan ini?",
        "Kamu pengen lihat TF, Soulmate, atau Karmic?",
      ],
      inputs: {
        dob_user: "date",
        dob_partner: "date",
        relationship_situation: "text",
      },
      sections: {
        soft_summary: "Ringkasan lembut tentang koneksi kalian.",
        emotional_pulse: "Energi batin yang lagi aktif antara kalian.",
        connection_type: "Twin Flames / Soulmate / Karmic + penjelasan modern.",
        growth_zone: "Area yang bisa diperbaiki bersama.",
        gentle_suggestion: "Saran lembut, natural, tidak formal.",
      },
      visual_compatibility: {
        items: [
          {
            id: "energy_sync",
            label: "Energi Kalian",
            value_type: "percentage",
            icon: "⚡",
          },
          {
            id: "emotional_flow",
            label: "Aliran Emosi",
            value_type: "status",
            options: ["stabil", "naik", "turun"],
            icon: "🌊",
          },
          {
            id: "connection_vibe",
            label: "Vibe Koneksi",
            value_type: "short_label",
            options: ["deep", "intense", "soft"],
            icon: "💕",
          },
        ],
      },
      mini_cards: [
        {
          id: "intuisi",
          title: "Intuisi",
          icon: "👁️",
          bind_to: "emotional_pulse",
        },
        {
          id: "koneksi",
          title: "Koneksi",
          icon: "🔄",
          bind_to: "connection_type",
        },
        { id: "growth", title: "Growth", icon: "🌱", bind_to: "growth_zone" },
      ],
      ctas: ["Mau lihat ritme minggu ini?", "Mau cek pola komunikasi kalian?"],
    },
    npd_toxic_dynamics: {
      id: "npd_toxic_v1",
      title: "NPD & Toxic Dynamics",
      subtitle:
        "Kenali pola hubungan yang bikin kamu lelah dan cara keluar dari siklusnya.",
      color: "muted_red",
      icon: "💔",
      guided_questions: [
        "Apa perilaku yang paling bikin kamu capek belakangan ini?",
        "Kamu merasa hubungan ini bikin kamu makin tenang atau makin gelisah?",
        "Kamu pengen lihat pola NPD, trauma bonding, atau red flags?",
      ],
      inputs: {
        relationship_situation: "text",
        partner_behavior: "text",
        user_feelings: "text",
      },
      sections: {
        soft_summary: "Ringkasan lembut tanpa judgement.",
        emotional_pulse: "Bagian mana yang bikin kamu lelah.",
        toxic_pattern_insight:
          "NPD / trauma bonding / avoidant / anxious / red flags.",
        clarity_zone: "Apa yang sebenarnya terjadi secara emosional.",
        gentle_direction: "Langkah kecil yang bisa kamu ambil.",
      },
      mini_cards: [
        {
          id: "intuisi",
          title: "Intuisi",
          icon: "👁️",
          bind_to: "emotional_pulse",
        },
        {
          id: "dinamika",
          title: "Dinamika",
          icon: "🔄",
          bind_to: "toxic_pattern_insight",
        },
        {
          id: "clarity",
          title: "Clarity",
          icon: "💡",
          bind_to: "clarity_zone",
        },
      ],
      ctas: [
        "Mau lihat pola komunikasi kalian?",
        "Mau cek apakah ini trauma bonding?",
      ],
    },
    relationship_growth: {
      id: "relationship_growth_v1",
      title: "Relationship Growth",
      subtitle:
        "Bangun hubungan lewat energi, tanggal lahir, dan cerita kalian.",
      color: "soft_blue",
      icon: "🤝",
      guided_questions: [
        "Hal apa yang ingin kamu perbaiki dalam hubungan kalian?",
        "Kalian lebih sering tenang atau tegang akhir-akhir ini?",
        "Kamu pengen lihat growth zone atau emotional rhythm?",
      ],
      inputs: {
        dob_user: "date",
        dob_partner: "date",
        relationship_situation: "text",
        improvement_goal: "text",
      },
      sections: {
        soft_summary: "Ringkasan hubungan kalian.",
        emotional_rhythm: "Ritme emosional kalian berdua.",
        energy_dynamics: "Bagaimana energi kalian saling mempengaruhi.",
        growth_zone: "Area yang bisa diperbaiki bersama.",
        gentle_suggestion: "Saran lembut untuk membangun hubungan.",
      },
      visual_compatibility: {
        items: [
          {
            id: "energy_sync",
            label: "Energi Kalian",
            value_type: "percentage",
            icon: "⚡",
          },
          {
            id: "emotional_flow",
            label: "Aliran Emosi",
            value_type: "status",
            options: ["stabil", "naik", "turun"],
            icon: "🌊",
          },
          {
            id: "connection_vibe",
            label: "Vibe Koneksi",
            value_type: "short_label",
            options: ["deep", "intense", "soft"],
            icon: "💕",
          },
        ],
      },
      mini_cards: [
        {
          id: "ritme",
          title: "Ritme",
          icon: "📊",
          bind_to: "emotional_rhythm",
        },
        {
          id: "energi",
          title: "Energi",
          icon: "⚡",
          bind_to: "energy_dynamics",
        },
        { id: "growth", title: "Growth", icon: "🌱", bind_to: "growth_zone" },
      ],
      ctas: ["Mau lihat ritme bulan ini?", "Mau cek pola komunikasi kalian?"],
    },
    energy_match_team: {
      id: "energy_match_team_v1",
      title: "Energy Match (Team)",
      subtitle:
        "Lihat dinamika tim dan cara bikin kerja bareng lebih harmonis.",
      color: "teal",
      icon: "👥",
      guided_questions: [
        "Tim kalian lagi di fase apa?",
        "Siapa leadernya?",
        "Apa tantangan terbesar tim sekarang?",
      ],
      inputs: {
        team_members: "member_list",
        team_situation: "text",
      },
      sections: {
        soft_summary: "Ringkasan dinamika tim.",
        team_pulse: "Energi yang lagi aktif dalam tim.",
        leader_member_dynamics: "Bagaimana pola kepemimpinan bekerja.",
        harmony_zone: "Area yang bisa bikin tim lebih selaras.",
        gentle_suggestion: "Saran untuk kerja bareng lebih baik.",
      },
      mini_cards: [
        { id: "pulse", title: "Pulse", icon: "📊", bind_to: "team_pulse" },
        {
          id: "leader",
          title: "Leader",
          icon: "👤",
          bind_to: "leader_member_dynamics",
        },
        {
          id: "harmony",
          title: "Harmony",
          icon: "🌱",
          bind_to: "harmony_zone",
        },
      ],
      ctas: ["Tambah anggota tim?", "Mau lihat dinamika minggu ini?"],
    },
  },
};

// ============ HELPER FUNCTIONS ============
const getModule = (moduleId: string): Module | undefined => {
  return MODULE_CONFIG.modules[moduleId];
};

const getToneForSection = (
  section: string,
  language: Language = "id"
): string[] => {
  const tonePack: {
    [lang: string]: { [key: string]: string[] };
  } = {
    id: {
      soft_summary: [
        "Koneksi kalian lagi bergerak dengan ritme yang tenang.",
        "Ada bagian dari cerita ini yang sebenarnya cuma butuh ruang.",
        "Situasi kamu sekarang wajar banget, dan kamu nggak sendirian.",
      ],
      emotional_pulse: [
        "Kamu lagi lebih peka sama hal-hal kecil.",
        "Ada perasaan yang kamu tahan cukup lama.",
        "Energi emosionalmu lagi naik turun, tapi masih dalam batas yang aman.",
      ],
      connection_or_dynamics: [
        "Energi kalian saling tarik-menarik dengan cara yang lembut.",
        "Ada pola komunikasi yang bisa diperbaiki pelan-pelan.",
      ],
      growth_or_harmony_zone: [
        "Bagian ini bisa jadi titik awal buat kalian berkembang.",
        "Kalian butuh ruang aman buat ngomong jujur tanpa takut salah.",
      ],
      gentle_suggestion: [
        "Mulai dari langkah yang paling ringan dulu.",
        "Pelan-pelan aja, kamu nggak harus buru-buru.",
      ],
    },
    en: {
      soft_summary: [
        "Your connection is moving with a calm rhythm right now.",
        "Part of this story really just needs some space.",
        "What you're going through is completely normal, and you're not alone.",
      ],
      emotional_pulse: [
        "You've been more sensitive to little things lately.",
        "There's a feeling you've been holding in for a while.",
        "Your emotional energy has been up and down, but still within a safe range.",
      ],
      connection_or_dynamics: [
        "Your energies are drawn to each other in a gentle way.",
        "There's a communication pattern that can be improved little by little.",
      ],
      growth_or_harmony_zone: [
        "This part can be a starting point for you to grow together.",
        "You need a safe space to speak honestly without fear of being wrong.",
      ],
      gentle_suggestion: [
        "Start with the lightest step first.",
        "Take it slow, there's no need to rush.",
      ],
    },
  };
  return tonePack[language]?.[section] || [];
};

// English fallback text for module sections, mirrored 1:1 with the
// Indonesian copy in MODULE_CONFIG so the offline/mock result never
// mixes languages.
const SECTION_FALLBACK_EN: { [moduleId: string]: { [key: string]: string } } =
  {
    twin_flames_soulmate_karmic: {
      soft_summary: "A gentle summary of your connection.",
      emotional_pulse: "The inner energy that's active between you two.",
      connection_type: "Twin Flames / Soulmate / Karmic + a modern explanation.",
      growth_zone: "Areas you can work on together.",
      gentle_suggestion: "Gentle, natural, non-formal suggestions.",
    },
    npd_toxic_dynamics: {
      soft_summary: "A gentle summary, without judgement.",
      emotional_pulse: "The part that's been draining you.",
      toxic_pattern_insight: "NPD / trauma bonding / avoidant / anxious / red flags.",
      clarity_zone: "What's actually happening emotionally.",
      gentle_direction: "A small step you can take.",
    },
    relationship_growth: {
      soft_summary: "A summary of your relationship.",
      emotional_rhythm: "The emotional rhythm between you two.",
      energy_dynamics: "How your energies influence each other.",
      growth_zone: "Areas you can work on together.",
      gentle_suggestion: "Gentle suggestions to build your relationship.",
    },
    energy_match_team: {
      soft_summary: "A summary of your team's dynamics.",
      team_pulse: "The energy currently active within the team.",
      leader_member_dynamics: "How the leadership pattern is working.",
      harmony_zone: "Areas that can make the team more aligned.",
      gentle_suggestion: "Suggestions for working together better.",
    },
  };

// ============ MAIN COMPONENT ============
const IndonesiaModules: React.FC = () => {
  // State
  const [currentStep, setCurrentStep] = useState<Step>("home");
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswers>({});
  const [formData, setFormData] = useState<FormData>({});
  const [result, setResult] = useState<ResultData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const language: Language = useIndonesiaV2Language();

  // Get current module and translations
  const currentModule = selectedModule ? getModule(selectedModule) : null;
  const appLang = useAppLanguage();
  const t = useLaneCopy("idmod", TRANSLATIONS);
  const tx = (key: string) => i18n.t(`laneCopy.idmodx.${key}`);

  // Navigation functions
  const goToHome = () => {
    setCurrentStep("home");
    setSelectedModule(null);
    setQuestionAnswers({});
    setFormData({});
    setResult(null);
  };

  // Get translated module title
  const getModuleTitle = (moduleKey: string): string => {
    const titles: { [key: string]: string } = {
      twin_flames_soulmate_karmic: t.tfTitle,
      npd_toxic_dynamics: t.npdTitle,
      relationship_growth: t.growthTitle,
      energy_match_team: t.teamTitle,
    };
    return titles[moduleKey] || moduleKey;
  };

  const getModuleSubtitle = (moduleKey: string): string => {
    const subtitles: { [key: string]: string } = {
      twin_flames_soulmate_karmic: t.tfSubtitle,
      npd_toxic_dynamics: t.npdSubtitle,
      relationship_growth: t.growthSubtitle,
      energy_match_team: t.teamSubtitle,
    };
    return subtitles[moduleKey] || "";
  };

  // Get translated question
  const getTranslatedQuestion = (moduleKey: string, index: number): string => {
    const questionMap: { [key: string]: string[] } = {
      twin_flames_soulmate_karmic: [t.phase, t.confusion, t.type],
      npd_toxic_dynamics: [t.behavior, t.feeling, t.pattern],
      relationship_growth: [t.improvement, t.mood, t.focus],
      energy_match_team: [t.teamPhase, t.leader, t.challenge],
    };
    const questions = questionMap[moduleKey] || [];
    return questions[index] || "";
  };

  // Get translated options
  const getOptions = (moduleKey: string, index: number): string[] => {
    const optionMap: { [key: string]: { [key: number]: string[] } } = {
      twin_flames_soulmate_karmic: {
        0: t.phaseOptions,
        2: t.connectionOptions,
      },
      npd_toxic_dynamics: {
        1: t.moodOptions,
        2: t.patternOptions,
      },
      relationship_growth: {
        1: t.moodOptions,
        2: t.focusOptions,
      },
      energy_match_team: {
        0: t.teamPhaseOptions,
      },
    };
    return optionMap[moduleKey]?.[index] || [];
  };

  // Get translated section title
  const getSectionTitle = (key: string): string => {
    const titles: { [key: string]: string } = {
      soft_summary: t.softSummary,
      emotional_pulse: t.emotionalPulse,
      connection_type: t.connectionType,
      growth_zone: t.growthZone,
      gentle_suggestion: t.gentleSuggestion,
      emotional_rhythm: t.emotionalRhythm,
      energy_dynamics: t.energyDynamics,
      toxic_pattern_insight: t.toxicPattern,
      clarity_zone: t.clarityZone,
      gentle_direction: t.gentleDirection,
      team_pulse: t.teamPulse,
      leader_member_dynamics: t.leaderDynamics,
      harmony_zone: t.harmonyZone,
    };
    return titles[key] || key.replace(/_/g, " ");
  };

  // Get translated CTA
  const getTranslatedCTA = (cta: string): string => {
    const ctaMap: { [key: string]: string } = {
      "Mau lihat ritme minggu ini?": t.weeklyRhythm,
      "Mau cek pola komunikasi kalian?": t.checkCommunication,
      "Mau cek apakah ini trauma bonding?": t.checkTraumaBonding,
      "Mau lihat ritme bulan ini?": t.monthlyRhythm,
      "Tambah anggota tim?": t.addTeamMember,
      "Mau lihat dinamika minggu ini?": t.weeklyDynamics,
    };
    return ctaMap[cta] || cta;
  };

  // Get translated placeholder
  const getPlaceholder = (fieldName: string): string => {
    const placeholders: { [key: string]: string } = {
      relationship_situation: t.relationshipPlaceholder,
      partner_behavior: t.behaviorPlaceholder,
      user_feelings: t.feelingsPlaceholder,
      improvement_goal: t.goalPlaceholder,
      team_situation: t.teamSituationPlaceholder,
    };
    return placeholders[fieldName] || `${t.enter}...`;
  };

  // Get translated field label
  const getFieldLabel = (fieldName: string): string => {
    const labels: { [key: string]: string } = {
      dob_user: t.dobUser,
      dob_partner: t.dobPartner,
      relationship_situation: t.relationshipSituation,
      partner_behavior: t.partnerBehavior,
      user_feelings: t.userFeelings,
      improvement_goal: t.improvementGoal,
      team_members: t.teamMembers,
      team_situation: t.teamSituation,
    };
    return labels[fieldName] || fieldName.replace(/_/g, " ");
  };

  // ============ STEP 1: HOME ============
  const renderHome = () => {
    const modules = MODULE_CONFIG.modules;

    return (
      <div
        className="min-h-screen py-8 px-4 [color-scheme:dark]"
        style={{
          background:
            "#0f0a14",
        }}
      >
        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(40px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          .animate-fade-in-up {
            animation: fadeInUp 0.7s ease-out forwards;
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          .card-glow {
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .card-glow:hover {
            transform: translateY(-12px) scale(1.02);
            box-shadow: 0 30px 60px rgba(139, 92, 246, 0.15);
          }
          .shimmer-text {
            background: linear-gradient(90deg, #a78bff, #d9ccff, #a78bff);
            background-size: 200% auto;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: shimmer 3s linear infinite;
          }
        `}</style>

        {/* Decorative Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-block p-4 rounded-full bg-white/[0.06] backdrop-blur-sm mb-4 animate-float">
            <span className="text-6xl">✨</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-3">
            <span className="shimmer-text">{t.appName}</span>
          </h1>
          <p className="text-[#f7f3fa] text-lg md:text-xl max-w-2xl mx-auto">
            {t.tagline}
          </p>
          <div className="flex justify-center gap-2 mt-4">
            <span className="px-3 py-1 bg-white/[0.06] backdrop-blur-sm rounded-full text-sm text-[#c8bfd1]">
              ✨ {t.indonesia}
            </span>
            <span className="px-3 py-1 bg-white/[0.06] backdrop-blur-sm rounded-full text-sm text-[#c8bfd1]">
              💫 {t.year}
            </span>
          </div>
        </div>

        {/* Module Cards */}
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-semibold text-[#f7f3fa] mb-6 text-center">
            {t.selectModule}
          </h2>
          <p className="text-center text-[#c8bfd1] mb-8">{t.chooseTopic}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(modules).map(([key, module], index) => {
              const colorMap: {
                [key: string]: { bg: string; border: string; shadow: string };
              } = {
                lavender: {
                  bg: "bg-[#1a131f] border border-white/10",
                  border: "border-purple-200",
                  shadow: "shadow-purple-100/50",
                },
                muted_red: {
                  bg: "bg-[#1a131f] border border-white/10",
                  border: "border-white/10",
                  shadow: "shadow-red-100/50",
                },
                soft_blue: {
                  bg: "bg-[#1a131f] border border-white/10",
                  border: "border-white/10",
                  shadow: "shadow-blue-100/50",
                },
                teal: {
                  bg: "bg-[#1a131f] border border-white/10",
                  border: "border-white/10",
                  shadow: "shadow-teal-100/50",
                },
              };
              const colors = colorMap[module.color] || colorMap.lavender;

              return (
                <div
                  key={key}
                  className={`
                    ${colors.bg} ${colors.border} ${colors.shadow}
                    backdrop-blur-sm border-2 rounded-2xl p-6 cursor-pointer card-glow
                    flex flex-col items-center text-center
                    min-h-[240px] justify-center
                    animate-fade-in-up
                  `}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => {
                    setSelectedModule(key);
                    setCurrentStep("questions");
                  }}
                >
                  <div className="text-5xl mb-4 bg-white/[0.06] rounded-full p-4 backdrop-blur-sm">
                    {module.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#f7f3fa] mb-2">
                    {getModuleTitle(key)}
                  </h3>
                  <p className="text-sm text-[#c8bfd1]/90 leading-relaxed">
                    {getModuleSubtitle(key)}
                  </p>
                  <div className="mt-4 text-[#d9ccff] text-sm font-medium flex items-center gap-1">
                    {t.clickToStart}
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ============ STEP 2: QUESTIONS ============
  const renderQuestions = () => {
    if (!currentModule || !selectedModule) return null;

    const questions = currentModule.guided_questions;

    const renderQuestionInput = (question: string, index: number) => {
      const options = getOptions(selectedModule, index);

      if (options.length > 0 && index !== 2) {
        // Dropdown for options
        return (
          <select
            value={questionAnswers[index] || ""}
            onChange={(e) =>
              setQuestionAnswers({
                ...questionAnswers,
                [index]: e.target.value,
              })
            }
            className="w-full px-5 py-3.5 bg-[#221a29] border-2 border-white/10 rounded-xl 
                       focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20 
                       transition-all duration-300 text-[#f7f3fa] text-base
                       hover:border-[#a78bff]/60 outline-none appearance-none
                       shadow-sm hover:shadow-md
                       bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2224%22 height=%2224%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%23a78bff%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Cpolyline points=%226 9 12 15 18 9%22/%3E%3C/svg%3E')]
                       bg-no-repeat bg-[length:24px] bg-[right_16px_center] pr-12"
          >
            <option value="">{t.select}</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      } else if (options.length > 0 && index === 2) {
        // Button group for connection type
        return (
          <div className="flex gap-3 flex-wrap">
            {options.map((opt) => (
              <button
                key={opt}
                className={`
                  px-6 py-2.5 rounded-full border-2 transition-all duration-300 font-medium text-sm
                  ${
                    questionAnswers[index] === opt
                      ? "bg-[#a78bff] text-[#0f0a14] border-transparent shadow-[0_0_22px_rgba(167,139,255,0.35)] "
                      : "bg-[#221a29] text-[#f7f3fa] border-white/10 hover:border-[#a78bff]/60 hover:bg-white/10"
                  }
                `}
                onClick={() =>
                  setQuestionAnswers({ ...questionAnswers, [index]: opt })
                }
              >
                {opt}
              </button>
            ))}
          </div>
        );
      } else {
        return (
          <input
            type="text"
            placeholder={t.tellHonestly}
            value={questionAnswers[index] || ""}
            onChange={(e) =>
              setQuestionAnswers({
                ...questionAnswers,
                [index]: e.target.value,
              })
            }
            className="w-full px-5 py-3.5 bg-[#221a29] border-2 border-white/10 rounded-xl 
                       focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20 
                       transition-all duration-300 text-[#f7f3fa] text-base
                       hover:border-[#a78bff]/60 outline-none placeholder-gray-400
                       shadow-sm hover:shadow-md"
          />
        );
      }
    };

    return (
      <div
        className="min-h-screen py-8 px-4 [color-scheme:dark]"
        style={{
          background:
            "#0f0a14",
        }}
      >
        <style>{`
          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(-30px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes pulseGlow {
            0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.1); }
            50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.2); }
          }
          .question-bubble {
            animation: slideInRight 0.5s ease-out forwards;
          }
          .question-bubble:nth-child(2) { animation-delay: 0.1s; }
          .question-bubble:nth-child(3) { animation-delay: 0.2s; }
          .pulse-glow {
            animation: pulseGlow 2s ease-in-out infinite;
          }
        `}</style>

        <div className="max-w-2xl mx-auto">
          {/* Back */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={goToHome}
              className="flex items-center gap-2 text-[#c8bfd1] hover:text-[#d9ccff] transition-all duration-300 hover:bg-white/[0.06] px-4 py-2.5 rounded-xl backdrop-blur-sm"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t.back}
            </button>
          </div>

          {/* Header */}
          <div className="text-center mb-8 animate-fade-in-up">
            <div className="text-6xl mb-3 bg-white/[0.06] backdrop-blur-sm rounded-full w-24 h-24 flex items-center justify-center mx-auto">
              {currentModule.icon}
            </div>
            <h2 className="text-3xl font-bold text-[#f7f3fa]">
              {getModuleTitle(selectedModule)}
            </h2>
            <p className="text-[#c8bfd1] mt-2 text-lg">
              {getModuleSubtitle(selectedModule)}
            </p>
            <div className="flex justify-center gap-2 mt-3">
              <span className="px-3 py-1 bg-white/[0.06] backdrop-blur-sm rounded-full text-xs text-[#c8bfd1]">
                {t.question} {Object.keys(questionAnswers).length}/
                {questions.length}
              </span>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-4">
            {questions.map((q, index) => {
              const translatedQ =
                getTranslatedQuestion(selectedModule, index) || q;
              return (
                <div
                  key={index}
                  className="bg-[#1a131f] border border-white/10 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-purple-400 question-bubble"
                >
                  <p className="text-[#f7f3fa] font-semibold text-lg mb-3 flex items-start gap-2">
                    <span className="text-[#d9ccff] font-bold">
                      {index + 1}.
                    </span>
                    {translatedQ}
                  </p>
                  <div className="question-input-wrapper">
                    {renderQuestionInput(q, index)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            className="w-full mt-8 px-6 py-4 bg-[#a78bff] 
                        text-[#0f0a14] font-semibold text-lg rounded-xl 
                       shadow-[0_0_22px_rgba(167,139,255,0.35)] hover:brightness-110 
                       transition-all duration-500 
                       disabled:opacity-50 disabled:cursor-not-allowed
                       active:scale-[0.98]
                       pulse-glow"
            onClick={() => setCurrentStep("input")}
            disabled={Object.keys(questionAnswers).length < questions.length}
            style={{ backgroundSize: "200% auto" }}
          >
            {Object.keys(questionAnswers).length >= questions.length ? (
              <span className="flex items-center justify-center gap-2">
                {t.continueToData}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>
            ) : (
              <span>{t.answerAll}</span>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ============ STEP 3: INPUT ============
  const renderInput = () => {
    if (!currentModule || !selectedModule) return null;

    const inputs = currentModule.inputs;

    const getTeamMembers = (fieldName: string): TeamMember[] => {
      const raw = formData[fieldName];
      return Array.isArray(raw) && raw.length > 0
        ? (raw as TeamMember[])
        : [emptyTeamMember(), emptyTeamMember()];
    };

    const updateTeamMember = (
      fieldName: string,
      idx: number,
      field: keyof TeamMember,
      value: string,
    ) => {
      const members = getTeamMembers(fieldName);
      const next = members.map((m, i) =>
        i === idx ? { ...m, [field]: value } : m,
      );
      setFormData({ ...formData, [fieldName]: next });
    };

    const addTeamMemberRow = (fieldName: string) => {
      const members = getTeamMembers(fieldName);
      if (members.length >= MAX_TEAM_MEMBERS) return;
      setFormData({
        ...formData,
        [fieldName]: [...members, emptyTeamMember()],
      });
    };

    const removeTeamMemberRow = (fieldName: string, idx: number) => {
      const members = getTeamMembers(fieldName);
      if (members.length <= 1) return;
      setFormData({
        ...formData,
        [fieldName]: members.filter((_, i) => i !== idx),
      });
    };

    const renderMemberList = (fieldName: string) => {
      const members = getTeamMembers(fieldName);
      return (
        <div className="space-y-4">
          {members.map((member, idx) => (
            <div
              key={idx}
              className="rounded-xl border-2 border-white/10 p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#d9ccff]">
                  {t.memberLabel} {idx + 1}
                </span>
                {members.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTeamMemberRow(fieldName, idx)}
                    className="text-[11px] font-semibold uppercase tracking-wide text-rose-500 hover:text-[#d9ccff]"
                  >
                    {t.removeMember}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#c8bfd1] mb-1">
                    {t.memberName}
                  </label>
                  <input
                    type="text"
                    placeholder={t.memberNamePlaceholder}
                    value={member.name}
                    onChange={(e) =>
                      updateTeamMember(fieldName, idx, "name", e.target.value)
                    }
                    className="w-full px-3.5 py-2.5 bg-[#221a29] border-2 border-white/10 rounded-lg
                               focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20
                               transition-all duration-300 text-[#f7f3fa] text-sm outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#c8bfd1] mb-1">
                    {t.memberDob}
                  </label>
                  <input
                    type="date"
                    value={member.dob}
                    onChange={(e) =>
                      updateTeamMember(fieldName, idx, "dob", e.target.value)
                    }
                    className="w-full px-3.5 py-2.5 bg-[#221a29] border-2 border-white/10 rounded-lg
                               focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20
                               transition-all duration-300 text-[#f7f3fa] text-sm outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#c8bfd1] mb-1">
                    {t.memberRole}
                  </label>
                  <input
                    type="text"
                    placeholder={t.memberRolePlaceholder}
                    value={member.role}
                    onChange={(e) =>
                      updateTeamMember(fieldName, idx, "role", e.target.value)
                    }
                    className="w-full px-3.5 py-2.5 bg-[#221a29] border-2 border-white/10 rounded-lg
                               focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20
                               transition-all duration-300 text-[#f7f3fa] text-sm outline-none"
                  />
                </div>
              </div>
            </div>
          ))}

          {members.length < MAX_TEAM_MEMBERS && (
            <button
              type="button"
              onClick={() => addTeamMemberRow(fieldName)}
              className="w-full rounded-xl py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border-2 border-dashed border-purple-300 text-[#d9ccff] hover:bg-white/10 transition-colors"
            >
              {t.addMemberBtn}
            </button>
          )}
        </div>
      );
    };

    const renderField = (fieldName: string, fieldType: string) => {
      const label = getFieldLabel(fieldName);
      const value = formData[fieldName] || "";

      switch (fieldType) {
        case "member_list":
          return renderMemberList(fieldName);
        case "date":
          return (
            <input
              type="date"
              value={(value as string) || ""}
              onChange={(e) =>
                setFormData({ ...formData, [fieldName]: e.target.value })
              }
              className="w-full px-5 py-3.5 bg-[#221a29] border-2 border-white/10 rounded-xl 
                         focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20 
                         transition-all duration-300 text-[#f7f3fa] text-base
                         hover:border-[#a78bff]/60 outline-none
                         shadow-sm hover:shadow-md"
            />
          );
        case "list":
          return (
            <input
              type="text"
              placeholder={t.enterName}
              value={(value as string) || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  [fieldName]: e.target.value.split(",").map((s) => s.trim()),
                })
              }
              className="w-full px-5 py-3.5 bg-[#221a29] border-2 border-white/10 rounded-xl 
                         focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20 
                         transition-all duration-300 text-[#f7f3fa] text-base
                         hover:border-[#a78bff]/60 outline-none placeholder-gray-400
                         shadow-sm hover:shadow-md"
            />
          );
        case "text":
        default:
          return (
            <textarea
              placeholder={getPlaceholder(fieldName)}
              value={(value as string) || ""}
              onChange={(e) =>
                setFormData({ ...formData, [fieldName]: e.target.value })
              }
              className="w-full px-5 py-3.5 bg-[#221a29] border-2 border-white/10 rounded-xl 
                         focus:border-[#a78bff] focus:ring-4 focus:ring-[#a78bff]/20 
                         transition-all duration-300 text-[#f7f3fa] text-base
                         hover:border-[#a78bff]/60 outline-none placeholder-gray-400 resize-y
                         shadow-sm hover:shadow-md"
              rows={3}
            />
          );
      }
    };

    return (
      <div
        className="min-h-screen py-8 px-4 [color-scheme:dark]"
        style={{
          background:
            "#0f0a14",
        }}
      >
        <div className="max-w-2xl mx-auto animate-fade-in-up">
          {/* Back */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setCurrentStep("questions")}
              className="flex items-center gap-2 text-[#c8bfd1] hover:text-[#d9ccff] transition-all duration-300 hover:bg-white/[0.06] px-4 py-2.5 rounded-xl backdrop-blur-sm"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t.backToQuestions}
            </button>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-5xl mb-3">{currentModule.icon}</div>
            <h2 className="text-3xl font-bold text-[#f7f3fa]">
              {getModuleTitle(selectedModule)}
            </h2>
            <p className="text-[#c8bfd1] mt-2 text-lg">{t.fillData}</p>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {Object.entries(inputs).map(([fieldName, fieldType]) => (
              <div
                key={fieldName}
                className="bg-[#1a131f] border border-white/10 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <label className="block text-sm font-semibold text-[#f7f3fa] uppercase tracking-wider mb-2 flex items-center gap-2">
                  <span className="text-[#d9ccff]">✦</span>
                  {getFieldLabel(fieldName)}
                </label>
                {renderField(fieldName, fieldType)}
              </div>
            ))}
          </div>

          {/* Generate Button */}
          <button
            className="w-full mt-8 px-6 py-4 bg-[#a78bff] 
                        text-[#0f0a14] font-semibold text-lg rounded-xl 
                       shadow-[0_0_22px_rgba(167,139,255,0.35)] hover:brightness-110 
                       transition-all duration-500 
                       disabled:opacity-50 disabled:cursor-not-allowed
                       active:scale-[0.98]"
            onClick={handleGenerate}
            disabled={loading}
            style={{ backgroundSize: "200% auto" }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-3">
                <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                {t.processing}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {t.seeResults}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ============ STEP 4: RESULT ============
  const renderResult = () => {
    if (!currentModule || !result || !selectedModule) return null;

    const sections = currentModule.sections;
    const visualComp = currentModule.visual_compatibility;
    const miniCards = currentModule.mini_cards;
    const ctas = currentModule.ctas;

    const getRandomValue = (item: any) => {
      if (item.value_type === "percentage") {
        return `${Math.floor(Math.random() * 40 + 60)}%`;
      }
      if (item.value_type === "status" && item.options) {
        const statusMap: { [key: string]: string[] } = {
          stabil: t.statusOptions,
          naik: t.statusOptions,
          turun: t.statusOptions,
        };
        const options = statusMap[item.options[0]] || item.options;
        return options[Math.floor(Math.random() * options.length)];
      }
      if (item.value_type === "short_label" && item.options) {
        const idx = Math.floor(Math.random() * item.options.length);
        return t.vibeOptions?.[idx] ?? item.options[idx];
      }
      return "";
    };

    // Get translated visual labels
    const getTranslatedLabel = (label: string): string => {
      const labelMap: { [key: string]: string } = {
        "Energi Kalian": appLang === "id" ? "Energi Kalian" : tx("visual.energy"),
        "Aliran Emosi": appLang === "id" ? "Aliran Emosi" : tx("visual.flow"),
        "Vibe Koneksi": appLang === "id" ? "Vibe Koneksi" : tx("visual.vibe"),
      };
      return labelMap[label] || label;
    };

    return (
      <div
        className="min-h-screen py-8 px-4 [color-scheme:dark]"
        style={{
          background:
            "#0f0a14",
        }}
      >
        <style>{`
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .result-section {
            animation: scaleIn 0.5s ease-out forwards;
          }
          .result-section:nth-child(2) { animation-delay: 0.1s; }
          .result-section:nth-child(3) { animation-delay: 0.2s; }
          .result-section:nth-child(4) { animation-delay: 0.3s; }
          .result-section:nth-child(5) { animation-delay: 0.4s; }
        `}</style>

        <div className="max-w-2xl mx-auto animate-fade-in-up">
          {/* Back */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setCurrentStep("input")}
              className="flex items-center gap-2 text-[#c8bfd1] hover:text-[#d9ccff] transition-all duration-300 hover:bg-white/[0.06] px-4 py-2.5 rounded-xl backdrop-blur-sm"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              {t.backToForm}
            </button>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-3">{currentModule.icon}</div>
            <h2 className="text-3xl font-bold text-[#f7f3fa]">
              {getModuleTitle(selectedModule)}
            </h2>
            <p className="text-[#c8bfd1] text-sm mt-2">{t.resultsForYou}</p>
          </div>

          {/* Mini Cards */}
          {miniCards && miniCards.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              {miniCards.map((card) => (
                <div
                  key={card.id}
                  className="bg-[#1a131f] border border-white/10 rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <div className="text-3xl mb-1">{card.icon}</div>
                  <p className="text-sm font-medium text-[#f7f3fa]">
                    {appLang === "id"
                      ? card.title
                      : (
                          {
                            intuisi: "intuition",
                            koneksi: "connection",
                            growth: "growth",
                            ritme: "rhythm",
                            energi: "energy",
                            pulse: "pulse",
                            leader: "leader",
                            harmony: "harmony",
                            dinamika: "dynamics",
                            clarity: "clarity",
                          } as Record<string, string>
                        )[card.id]
                        ? tx(
                            `card.${(
                              {
                                intuisi: "intuition",
                                koneksi: "connection",
                                ritme: "rhythm",
                                energi: "energy",
                                dinamika: "dynamics",
                              } as Record<string, string>
                            )[card.id] || card.id}`,
                          )
                        : card.title}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Visual Compatibility */}
          {visualComp && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              {visualComp.items.map((item) => (
                <div
                  key={item.id}
                  className="bg-[#1a131f] border border-white/10 rounded-xl p-4 text-center shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-3xl mb-1">{item.icon}</div>
                  <p className="text-xs font-semibold text-[#c8bfd1] uppercase tracking-wider mb-1">
                    {getTranslatedLabel(item.label)}
                  </p>
                  <p className="text-2xl font-bold text-transparent bg-clip-text text-[#d9ccff]">
                    {getRandomValue(item)}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Result Sections */}
          <div className="space-y-4 mb-6">
            {Object.entries(sections).map(([key, value]) => {
              const iconMap: { [key: string]: string } = {
                soft_summary: "📝",
                emotional_pulse: "💓",
                connection_type: "🔗",
                growth_zone: "🌱",
                gentle_suggestion: "💡",
                emotional_rhythm: "🎵",
                energy_dynamics: "⚡",
                toxic_pattern_insight: "🔍",
                clarity_zone: "✨",
                gentle_direction: "🧭",
                team_pulse: "📊",
                leader_member_dynamics: "👥",
                harmony_zone: "🎯",
              };
              const colorMap: { [key: string]: string } = {
                soft_summary: "border-purple-400",
                emotional_pulse: "border-pink-400",
                connection_type: "border-blue-400",
                growth_zone: "border-green-400",
                gentle_suggestion: "border-yellow-400",
                emotional_rhythm: "border-indigo-400",
                energy_dynamics: "border-orange-400",
                toxic_pattern_insight: "border-red-400",
                clarity_zone: "border-cyan-400",
                gentle_direction: "border-teal-400",
                team_pulse: "border-purple-400",
                leader_member_dynamics: "border-blue-400",
                harmony_zone: "border-green-400",
              };

              return (
                <div
                  key={key}
                  className={`bg-[#1a131f] border border-white/10 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 ${colorMap[key] || "border-purple-400"} result-section`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{iconMap[key] || "✨"}</span>
                    <h4 className="text-sm font-semibold text-[#c8bfd1] uppercase tracking-wider">
                      {getSectionTitle(key)}
                    </h4>
                  </div>
                  <p className="text-[#f7f3fa] leading-relaxed text-base">
                    {result[key] ||
                      (appLang !== "id"
                        ? SECTION_FALLBACK_EN[selectedModule]?.[key] || value
                        : value)}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTAs */}
          <div className="flex flex-wrap gap-3 justify-center mb-6">
            {ctas.map((cta, index) => (
              <button
                key={index}
                className="px-6 py-3 bg-[#1a131f] border-2 border-white/10 rounded-xl hover:border-[#a78bff]/60 hover:bg-white/10 transition-all duration-300 font-medium text-[#f7f3fa] hover:shadow-md"
              >
                {getTranslatedCTA(cta)}
              </button>
            ))}
          </div>

          {/* Back to Home Button */}
          <button
            className="w-full px-6 py-4 bg-gray-200/80 backdrop-blur-sm text-[#f7f3fa] font-semibold text-lg rounded-xl hover:bg-gray-300/80 transition-all duration-300 hover:shadow-lg"
            onClick={goToHome}
          >
            ← {t.backToMenu}
          </button>
        </div>
      </div>
    );
  };

  // ============ API CALL ============
  const handleGenerate = async () => {
    if (!selectedModule) return;

    setLoading(true);
    try {
      const payload = {
        module: selectedModule,
        questions: questionAnswers,
        form_data: formData,
        language: appLang, // The reading is written in the user's preferred language
      };

      const response = await api.post("/generate-reading", payload);

      setResult(response.data);
      setCurrentStep("result");
    } catch (error) {
      console.error("Error generating reading:", error);
      if (currentModule) {
        const mockResult: ResultData = {};
        Object.keys(currentModule.sections).forEach((key) => {
          const tones = getToneForSection(key, language);
          if (tones.length > 0) {
            mockResult[key] = tones[Math.floor(Math.random() * tones.length)];
          } else if (appLang !== "id") {
            mockResult[key] =
              SECTION_FALLBACK_EN[selectedModule]?.[key] ||
              currentModule.sections[key];
          } else {
            mockResult[key] = currentModule.sections[key];
          }
        });
        setResult(mockResult);
        setCurrentStep("result");
      }
    } finally {
      setLoading(false);
    }
  };

  // ============ RENDER ============
  switch (currentStep) {
    case "home":
      return renderHome();
    case "questions":
      return renderQuestions();
    case "input":
      return renderInput();
    case "result":
      return renderResult();
    default:
      return renderHome();
  }
};

export default IndonesiaModules;
